import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, User, Mail, CreditCard, MapPin, Star, Trophy,
  BookOpen, Eye, Calendar, ExternalLink, ShieldCheck, RefreshCw,
  LogIn, Send, Zap, X, Ban, RotateCcw,
} from "lucide-react";

const TIER_CHOICES = [
  { value: "", label: "Inherit subscription (clear override)" },
  { value: "free", label: "Free" },
  { value: "pro", label: "Pro" },
  { value: "hustler", label: "Hustler" },
  { value: "team", label: "Team" },
];

const STAGE_COLORS = {
  New: "bg-slate-100 text-slate-700",
  "Live Lead": "bg-sky-100 text-sky-700",
  "Under Contract": "bg-amber-100 text-amber-700",
  Won: "bg-emerald-100 text-emerald-700",
  Dead: "bg-rose-100 text-rose-700",
};

export default function AdminUserDeepView() {
  const { userId } = useParams();
  const nav = useNavigate();
  const { startImpersonation } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tierBusy, setTierBusy] = useState(false);
  const [tierModal, setTierModal] = useState(false);
  const [suspendBusy, setSuspendBusy] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/users/${userId}/deep-view`);
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load user");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { refresh(); }, [refresh]);

  const onImpersonate = async () => {
    if (!data) return;
    if (!window.confirm(`Sign in as ${data.user.email}? You'll see their workspace until you click Exit.`)) return;
    try {
      await startImpersonation(data.user.id);
      toast.success(`Now viewing as ${data.user.email}`);
      nav("/v3", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impersonation failed");
    }
  };

  const onAdjustTier = () => setTierModal(true);

  const onSaveTier = async (newOverride) => {
    setTierBusy(true);
    try {
      await api.patch(`/admin/users/${data.user.id}`, { tier_override: newOverride });
      toast.success(newOverride ? `Tier override set to "${newOverride}"` : "Tier override cleared");
      setTierModal(false);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update tier");
    } finally {
      setTierBusy(false);
    }
  };

  const onToggleSuspend = async () => {
    if (!data) return;
    const next = !data.user.suspended;
    const verb = next ? "suspend" : "reinstate";
    if (!window.confirm(`${next ? "Suspend" : "Reinstate"} ${data.user.email}?${next ? " They won't be able to log in until reinstated." : ""}`)) return;
    setSuspendBusy(true);
    try {
      await api.patch(`/admin/users/${data.user.id}`, { suspended: next });
      toast.success(`User ${verb}d`);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Failed to ${verb} user`);
    } finally {
      setSuspendBusy(false);
    }
  };

  const onResendEmail = async (email) => {
    if (!email?.id) return;
    if (!window.confirm(`Resend "${email.subject || email.kind}" to ${data.user.email}?`)) return;
    try {
      const r = await api.post(`/admin/emails/${email.id}/resend`);
      if (r.data?.sent) {
        toast.success(`Re-sent to ${r.data.to}`);
      } else {
        toast.warning(`Re-fired but Resend declined (likely rate-limit or test-mode restriction).`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Resend failed");
    }
  };

  if (loading || !data) {
    return (
      <div className="card p-12 text-center text-[var(--ink-2)]" data-testid="admin-user-deep-view-loading">
        <Loader2 size={20} className="animate-spin inline" /> Loading…
      </div>
    );
  }

  const u = data.user;
  const sub = u.subscription || {};

  return (
    <div className="flex flex-col gap-4" data-testid="admin-user-deep-view">
      <Link to="/app/admin/users" className="text-sm text-[var(--ink-2)] hover:text-[var(--primary)] inline-flex items-center gap-1 w-fit">
        <ArrowLeft size={14} /> Back to Users
      </Link>

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs text-[var(--ink-2)] uppercase tracking-wide flex items-center gap-1.5">
            <User size={12} /> Admin · User Deep-View
          </div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight mt-1">{u.name || u.email}</h1>
          <div className="text-sm text-[var(--ink-2)] mt-1 flex items-center gap-3 flex-wrap">
            <span className="inline-flex items-center gap-1"><Mail size={11} /> {u.email}</span>
            {u.role === "admin" && <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-violet-100 text-violet-800 uppercase tracking-wide inline-flex items-center gap-1"><ShieldCheck size={10} /> Admin</span>}
            {u.suspended && <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-rose-100 text-rose-800 uppercase tracking-wide">Suspended</span>}
          </div>
        </div>
        <button type="button" className="btn btn-ghost" onClick={refresh} data-testid="user-deep-refresh">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Quick Actions strip */}
      <div className="card p-3 flex items-center gap-2 flex-wrap" data-testid="user-deep-quick-actions">
        <div className="text-[10px] uppercase tracking-widest text-[var(--ink-3)] font-bold inline-flex items-center gap-1 mr-2">
          <Zap size={11} /> Quick actions
        </div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={onImpersonate}
          disabled={u.role === "admin"}
          title={u.role === "admin" ? "Cannot impersonate another admin" : "Sign in as this user"}
          data-testid="user-deep-impersonate"
        >
          <LogIn size={14} /> Impersonate
        </button>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={onAdjustTier}
          disabled={tierBusy}
          data-testid="user-deep-adjust-tier"
        >
          {tierBusy ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
          Adjust tier
        </button>
        <button
          type="button"
          className={"btn " + (u.suspended ? "btn-primary" : "btn-ghost")}
          onClick={onToggleSuspend}
          disabled={suspendBusy || u.role === "admin"}
          title={u.role === "admin" ? "Cannot suspend an admin" : (u.suspended ? "Reinstate this user" : "Suspend this user from logging in")}
          data-testid="user-deep-suspend"
        >
          {suspendBusy
            ? <Loader2 size={14} className="animate-spin" />
            : (u.suspended ? <RotateCcw size={14} /> : <Ban size={14} />)}
          {u.suspended ? "Reinstate" : "Suspend"}
        </button>
        <span className="text-[10px] text-[var(--ink-3)] ml-auto">
          To resend a specific email, hover any row in the Email Log →
        </span>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KPI label="Tier" value={u.subscription?.tier || "free"} sub={sub.status || "no plan"} accent="emerald" />
        <KPI label="Leads" value={data.leads.total} sub={`${data.leads.recent.length} recent`} accent="sky" />
        <KPI label="Won deals" value={data.deals.won_count} sub={fmtMoney(data.deals.won_value_usd * 100)} accent="violet" />
        <KPI label="Resource views" value={data.resources.views} sub={`${data.resources.track_completions.length} tracks`} accent="amber" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Subscription */}
        <Card title="Subscription" icon={CreditCard} testid="user-deep-subscription">
          <Row label="Tier" value={sub.tier || "free"} />
          <Row label="Status" value={sub.status || "—"} accent={sub.status === "active" ? "emerald" : sub.status === "trialing" ? "sky" : null} />
          <Row label="Stripe customer" value={sub.customer_id ? <span className="font-mono text-[10px]">{sub.customer_id}</span> : "—"} />
          {sub.cancel_at_period_end && <Row label="Cancelling" value="At period end" accent="rose" />}
          {sub.activated_at && <Row label="Activated" value={fmtDate(sub.activated_at)} />}
        </Card>

        {/* Recent transactions */}
        <Card title="Stripe Transactions" icon={CreditCard} testid="user-deep-transactions">
          {data.transactions.length === 0 ? (
            <Empty label="No transactions yet." />
          ) : (
            <ul className="space-y-1.5 text-xs">
              {data.transactions.map((t, i) => (
                <li key={i} className="flex items-center justify-between gap-2 border-b last:border-b-0 pb-1">
                  <div className="min-w-0">
                    <div className="font-semibold">{t.kind || "payment"} · {t.status}</div>
                    <div className="text-[10px] text-[var(--ink-3)] font-mono truncate">{t.stripe_id}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="tabular-nums font-semibold">{fmtMoney(t.amount_cents || 0)}</div>
                    <div className="text-[10px] text-[var(--ink-3)]">{relTime(t.created_at)}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* Leads by stage */}
        <Card title="Leads" icon={MapPin} testid="user-deep-leads">
          {data.leads.total === 0 ? (
            <Empty label="No leads yet." />
          ) : (
            <>
              <div className="flex flex-wrap gap-1 mb-2">
                {Object.entries(data.leads.by_stage).map(([stage, n]) => (
                  <span key={stage} className={"px-2 py-0.5 rounded text-[10px] font-bold uppercase " + (STAGE_COLORS[stage] || "bg-slate-100 text-slate-700")}>
                    {stage}: {n}
                  </span>
                ))}
              </div>
              <div className="text-[10px] uppercase tracking-wide text-[var(--ink-3)] mb-1">Recent</div>
              <ul className="space-y-1 text-xs">
                {data.leads.recent.map((l) => (
                  <li key={l.id} className="flex items-center justify-between gap-2 border-b last:border-b-0 pb-1">
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{l.address || l.seller_name || "—"}</div>
                      <div className="text-[10px] text-[var(--ink-3)]">{relTime(l.created_at)}</div>
                    </div>
                    <span className={"px-2 py-0.5 rounded text-[10px] font-bold uppercase shrink-0 " + (STAGE_COLORS[l.stage] || "bg-slate-100 text-slate-700")}>
                      {l.stage}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </Card>

        {/* Power-team buyers */}
        <Card title="Cash Buyers" icon={User} testid="user-deep-buyers">
          {data.buyers.total === 0 ? (
            <Empty label="No buyers in Power Team yet." />
          ) : (
            <>
              <div className="text-sm font-semibold mb-2">{data.buyers.total.toLocaleString()} total</div>
              <ul className="space-y-1 text-xs">
                {data.buyers.recent.map((b) => (
                  <li key={b.id} className="border-b last:border-b-0 pb-1">
                    <div className="font-semibold truncate">{b.name || b.email || "—"}</div>
                    <div className="text-[10px] text-[var(--ink-3)]">{b.email} · {b.phone || "no phone"}</div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </Card>

        {/* Email log */}
        <Card title="Email Log" icon={Mail} testid="user-deep-emails">
          {data.emails.length === 0 ? (
            <Empty label="No emails sent yet." />
          ) : (
            <ul className="space-y-1 text-xs max-h-80 overflow-y-auto">
              {data.emails.map((e, i) => (
                <li key={i} className="border-b last:border-b-0 pb-1 group">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold truncate flex-1">{e.subject || e.kind}</div>
                    <span className={"shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase " + (e.status === "sent" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700")}>
                      {e.status}
                    </span>
                    <button
                      type="button"
                      onClick={() => onResendEmail(e)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity text-[10px] font-bold uppercase tracking-wide text-[var(--primary)] hover:underline inline-flex items-center gap-0.5"
                      title="Resend this email"
                      data-testid="user-deep-resend-email"
                    >
                      <Send size={10} /> Resend
                    </button>
                  </div>
                  <div className="text-[10px] text-[var(--ink-3)] flex items-center gap-2 flex-wrap">
                    <span>{e.kind}</span>
                    {e.opened_at && <span className="inline-flex items-center gap-0.5"><Eye size={9} /> opened</span>}
                    {e.clicked_at && <span className="inline-flex items-center gap-0.5"><ExternalLink size={9} /> clicked</span>}
                    {e.metadata?.variant && <span>· Variant {e.metadata.variant}</span>}
                    <span className="ml-auto">{relTime(e.created_at)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* Resources */}
        <Card title="Resources Engagement" icon={BookOpen} testid="user-deep-resources">
          <Row label="Total views" value={data.resources.views} bold />
          <Row label="Favorites" value={data.resources.favorites} accent={data.resources.favorites > 0 ? "amber" : null} />
          <Row label="Tracks completed" value={data.resources.track_completions.length} accent={data.resources.track_completions.length > 0 ? "emerald" : null} />
          {data.resources.track_completions.length > 0 && (
            <div className="mt-2 pt-2 border-t border-slate-100">
              <div className="text-[10px] uppercase tracking-wide text-[var(--ink-3)] mb-1">Track completions</div>
              <ul className="space-y-0.5 text-xs">
                {data.resources.track_completions.map((tc, i) => (
                  <li key={i} className="flex items-center justify-between gap-2">
                    <span className="inline-flex items-center gap-1"><Trophy size={10} className="text-amber-500" /> {tc.track_id}</span>
                    <span className="text-[10px] text-[var(--ink-3)]">{relTime(tc.completed_at)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Card>
      </div>

      <div className="text-[10px] text-[var(--ink-3)] text-center pt-2">
        User ID: <span className="font-mono">{u.id}</span> · Created {fmtDate(u.created_at)}
      </div>

      {tierModal && (
        <TierAdjustModal
          currentOverride={u.tier_override || ""}
          subTier={u.subscription?.tier || "free"}
          userEmail={u.email}
          onClose={() => setTierModal(false)}
          onSave={onSaveTier}
          busy={tierBusy}
        />
      )}
    </div>
  );
}

function TierAdjustModal({ currentOverride, subTier, userEmail, onClose, onSave, busy }) {
  const [value, setValue] = useState(currentOverride);
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="tier-adjust-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <div className="font-display font-extrabold text-lg leading-tight">Adjust Tier</div>
            <div className="text-xs text-[var(--ink-2)] mt-0.5">{userEmail}</div>
          </div>
          <button type="button" onClick={onClose} className="p-1 hover:bg-slate-100 rounded">
            <X size={16} />
          </button>
        </div>
        <div className="p-4 flex flex-col gap-3">
          <div className="text-xs text-[var(--ink-2)] bg-slate-50 rounded p-3 leading-relaxed">
            <div className="flex items-center justify-between mb-1">
              <span className="uppercase tracking-wide text-[10px] font-bold">Subscription tier</span>
              <span className="font-bold">{subTier}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="uppercase tracking-wide text-[10px] font-bold">Current override</span>
              <span className="font-bold">{currentOverride || "—"}</span>
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide font-bold text-[var(--ink-2)] mb-1.5">
              Set tier override
            </div>
            <div className="flex flex-col gap-1.5">
              {TIER_CHOICES.map((c) => (
                <label key={c.value} className="flex items-center gap-2 p-2 rounded hover:bg-slate-50 cursor-pointer text-sm">
                  <input
                    type="radio"
                    name="tier"
                    value={c.value}
                    checked={value === c.value}
                    onChange={() => setValue(c.value)}
                    data-testid={`tier-radio-${c.value || "clear"}`}
                  />
                  {c.label}
                </label>
              ))}
            </div>
          </div>
          <div className="text-[10px] text-[var(--ink-3)] leading-relaxed">
            Override takes precedence over subscription tier. Use to manually grant access
            (refunds, comp accounts, support cases) without touching Stripe.
          </div>
        </div>
        <div className="p-4 border-t flex justify-end gap-2">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={() => onSave(value)} disabled={busy} data-testid="tier-modal-save">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function KPI({ label, value, sub, accent }) {
  const palette = {
    emerald: "bg-emerald-50 text-emerald-700",
    sky: "bg-sky-50 text-sky-700",
    violet: "bg-violet-50 text-violet-700",
    amber: "bg-amber-50 text-amber-700",
  }[accent] || "bg-slate-50 text-slate-700";
  return (
    <div className={"rounded-lg p-3 " + palette}>
      <div className="text-[10px] uppercase tracking-wide opacity-80">{label}</div>
      <div className="font-display font-black text-xl mt-0.5 tabular-nums">{value}</div>
      <div className="text-[10px] mt-0.5 opacity-70">{sub}</div>
    </div>
  );
}

function Card({ title, icon: Icon, children, testid }) {
  return (
    <div className="card p-4" data-testid={testid}>
      <div className="text-sm font-display font-bold mb-3 pb-2 border-b flex items-center gap-1.5">
        <Icon size={14} className="text-[var(--ink-2)]" /> {title}
      </div>
      {children}
    </div>
  );
}

function Row({ label, value, bold = false, accent = null }) {
  const accentClass = {
    emerald: "text-emerald-700",
    rose: "text-rose-700",
    amber: "text-amber-700",
    sky: "text-sky-700",
  }[accent] || "";
  return (
    <div className="flex items-center justify-between text-sm py-0.5">
      <div className="text-[var(--ink-2)]">{label}</div>
      <div className={"tabular-nums " + (bold ? "font-display font-black text-base" : "font-semibold") + " " + accentClass}>{value}</div>
    </div>
  );
}

function Empty({ label }) {
  return <div className="text-xs text-[var(--ink-3)] py-3 text-center">{label}</div>;
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function relTime(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const diff = Date.now() - then;
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
}
